import React, { useEffect, useState } from "react";
import { TowerDashboard } from "./features/tower/TowerDashboard";
import type { Meta, Kpis, Breakdown, Order } from "./features/tower/types";
import {
	currentUser,
	onLogin,
	onLogout,
	openLogin,
	closeLogin,
	logout,
} from "./auth/netlifyIdentity";

type ResolveActionType =
	| "New driver assigned"
	| "Kitchen remaking"
	| "Moved to the front of the que"
	| "Order Delivered"
	| "Order Cancelled";

export default function ControlTower() {
	const [user, setUser] = useState<any>(null);

	const [meta, setMeta] = useState<Meta>({
		date: "--",
		lastRefresh: "--",
	});

	const [kpis, setKpis] = useState<Kpis>({
		inFlight: "0",
		breached: "0",
		totalActive: "0",
		otherDays: "0",
	});

	const [breakdown, setBreakdown] = useState<Breakdown>({
		driver: "0",
		kitchen: "0",
		inTransit: "0",
		unclear: "0",
	});

	const [orders, setOrders] = useState<Order[]>([]);
	const [feedback, setFeedback] = useState({
		text: "",
		type: "",
	});

	useEffect(() => {
		// Save any deep-link hash before login wipes it (e.g. Freshdesk links
		// like pmmr.netlify.app/#footage?order=9C921). After login succeeds we
		// restore it so the footage tab opens pre-loaded with that order.
		const hash = window.location.hash;
		if (hash && hash.includes("?") && !currentUser()) {
			sessionStorage.setItem("intended_url", hash);
		}

		setUser(currentUser());

		onLogin((u) => {
			setUser(u);
			closeLogin();
			// Restore the intended deep-link after login
			const intended = sessionStorage.getItem("intended_url");
			if (intended) {
				sessionStorage.removeItem("intended_url");
				window.location.hash = intended;
			}
		});

		onLogout(() => {
			setUser(null);
		});
	}, []);

	const isAuthenticated = Boolean(user);

	const userDisplayName: string =
		user?.user_metadata?.full_name ||
		user?.user_metadata?.name ||
		user?.email ||
		"";

	const userEmail: string = user?.email || "";

	const handleLogout = () => logout();

	const fetchTowerData = async () => {
		if (!isAuthenticated) return;

		try {
			const response = await fetch("/.netlify/functions/get-control-tower");
			const data = await response.json();

			setMeta(
				data?.meta ?? {
					date: "--",
					lastRefresh: "--",
				}
			);

			setKpis(
				data?.kpis ?? {
					inFlight: "0",
					breached: "0",
					totalActive: "0",
					otherDays: "0",
				}
			);

			setBreakdown(
				data?.breakdown ?? {
					driver: "0",
					kitchen: "0",
					inTransit: "0",
					unclear: "0",
				}
			);

			setOrders(Array.isArray(data?.orders) ? data.orders : []);
		} catch (error) {
			console.error("Sync error", error);
		}
	};

	useEffect(() => {
		if (!isAuthenticated) return;

		fetchTowerData();

		const interval = setInterval(fetchTowerData, 30000);
		return () => clearInterval(interval);
	}, [isAuthenticated]);

	const resolveOrder = async (
		targetOrderId: string,
		actionType: ResolveActionType
	) => {
		if (!userDisplayName.trim()) {
			setFeedback({
				text: "No user identity found.",
				type: "error",
			});
			return;
		}

		const removed = orders.find((o) => o.id === targetOrderId) ?? null;
		const removedIndex = orders.findIndex((o) => o.id === targetOrderId);

		setOrders((prev) => prev.filter((o) => o.id !== targetOrderId));

		setFeedback({
			text: "",
			type: "pending",
		});

		try {
			const response = await fetch(
				"/.netlify/functions/resolve-order",
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						orderId: targetOrderId,
						name: userDisplayName,
						type: actionType,
					}),
				}
			);

			if (!response.ok) {
				throw new Error(await response.text());
			}

			setFeedback({
				text: "",
				type: "success",
			});

			setTimeout(fetchTowerData, 500);
		} catch (error) {
			if (removed) {
				setOrders((prev) => {
					const next = [...prev];
					const idx = Math.max(
						0,
						Math.min(removedIndex, next.length)
					);
					next.splice(idx, 0, removed);
					return next;
				});
			}

			setFeedback({
				text: "",
				type: "error",
			});
		}
	};

	// LOGIN SCREEN — Deliverect-inspired light theme
	if (!isAuthenticated) {
		return (
			<>
				<style>{`
					@keyframes ot-fade-in {
						from { opacity: 0; transform: translateY(12px); }
						to { opacity: 1; transform: translateY(0); }
					}
					.ot-login-btn {
						transition: all 0.15s ease;
					}
					.ot-login-btn:hover {
						filter: brightness(1.06);
						box-shadow: 0 8px 24px rgba(21,128,61,0.2) !important;
					}
				`}</style>

				<div
					style={{
						minHeight: "100vh",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						padding: 24,
						background: "var(--ct-bg)",
						fontFamily:
							'"Rubik", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
					}}
				>
					<div
						style={{
							width: "100%",
							maxWidth: 420,
							animation: "ot-fade-in 0.4s ease",
						}}
					>
						{/* brand */}
						<div
							style={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								gap: 16,
								marginBottom: 32,
							}}
						>
							<div
								style={{
									width: 52,
									height: 52,
									borderRadius: 14,
									background: "var(--ct-accent)",
									color: "#fff",
									display: "grid",
									placeItems: "center",
									fontSize: 24,
									fontWeight: 700,
								}}
							>
								O
							</div>
							<div style={{ textAlign: "center" }}>
								<div
									style={{
										fontSize: 22,
										fontWeight: 700,
										letterSpacing: "-0.02em",
										color: "var(--ct-ink)",
									}}
								>
									Orderlords
								</div>
								<div
									style={{
										fontSize: 14,
										color: "var(--ct-ink-3)",
										marginTop: 2,
									}}
								>
									Control Tower
								</div>
							</div>
						</div>

						{/* card */}
						<div
							style={{
								background: "var(--ct-surface)",
								border: "1px solid var(--ct-line)",
								borderRadius: 12,
								padding: "32px 28px",
								boxShadow: "0 4px 24px rgba(15,23,42,0.06)",
							}}
						>
							<div
								style={{
									fontSize: 16,
									fontWeight: 600,
									color: "var(--ct-ink)",
									textAlign: "center",
									marginBottom: 6,
								}}
							>
								Sign in to continue
							</div>
							<div
								style={{
									fontSize: 13,
									color: "var(--ct-ink-3)",
									textAlign: "center",
									marginBottom: 28,
									lineHeight: 1.5,
								}}
							>
								Access is restricted to authorised personnel only.
							</div>

							<button
								onClick={openLogin}
								className="ot-login-btn"
								style={{
									width: "100%",
									padding: "13px 20px",
									borderRadius: 9,
									border: "none",
									background: "var(--ct-accent)",
									color: "#ffffff",
									fontWeight: 600,
									fontSize: 15,
									fontFamily: "inherit",
									cursor: "pointer",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: 10,
								}}
							>
								<svg
									width="18"
									height="18"
									viewBox="0 0 48 48"
									fill="none"
								>
									<path
										d="M44.5 20H24v8.5h11.8C34.7 33.9 30.1 37 24 37c-7.2 0-13-5.8-13-13s5.8-13 13-13c3.1 0 5.9 1.1 8.1 2.9l6.4-6.4C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22c11 0 21-8 21-22 0-1.3-.2-2.7-.5-4z"
										fill="#fff"
										fillOpacity="0.9"
									/>
								</svg>
								Sign in with Google
							</button>

							{feedback.text && (
								<div
									style={{
										marginTop: 16,
										textAlign: "center",
										fontSize: 13,
										color:
											feedback.type === "error"
												? "var(--ct-crit)"
												: "var(--ct-ink-3)",
									}}
								>
									{feedback.text}
								</div>
							)}
						</div>

						<div
							style={{
								textAlign: "center",
								marginTop: 20,
								fontSize: 12,
								color: "var(--ct-ink-3)",
							}}
						>
							Orderlords Kitchen Operations · Cape Town
						</div>
					</div>
				</div>
			</>
		);
	}

	// MAIN DASHBOARD
	return (
		<TowerDashboard
			meta={meta}
			kpis={kpis}
			breakdown={breakdown}
			orders={orders}
			fetchTowerData={fetchTowerData}
			onTakeAction={resolveOrder}
			userDisplayName={userDisplayName}
			userEmail={userEmail}
			onLogout={handleLogout}
		/>
	);
}
